<template>
    <div class="section__title__box">
        <h4 class="title">Transcription Feedback</h4>
        <div class="rlms-table-filters">
            <div class="scrollable-container">
                <div class="head__title__wrap">
                    <div class="title__select__box">
                        <select class="select" v-model="feedbackType">
                            <option value="">All</option>
                            <option value="incorrect">Incorrect</option>
                            <option value="correct">Correct</option>
                        </select>
                    </div>
                </div>
            </div>
            <div class="rlms-tf-actions" v-if="false">
                <div class="input-icon-group input-icon-right">
                    <input type="search" placeholder="Search..." v-model.trim="search" @input="handleSearch">
                    <img class="rlms-input-icon" src="@/assets/images/search.svg">
                </div>
            </div>
        </div>
    </div>

    <div class="rlms-table" style="width: 100%;">
        <div class="rlms-table-inner">
            <table>
                <thead>
                    <tr>
                        <th>S.No</th>
                        <th>Question</th>
                        <th>Feedback</th>
                        <th>AI Transcription</th>
                        <th>Reported By</th>
                        <th>Action</th>
                    </tr>
                </thead>
                <tbody>
                    <template v-if="orders?.length > 0">
                        <tr v-for="(order, index) in orders" :key="order?.id">
                            <!-- ✅ Serial number -->
                            <td>#{{ getSerialNumber(index) }}</td>

                            <td>
                                <div class="data__test table__row__item">
                                    <label>{{ order.question }}</label>
                                    <span v-if="order.consonant">{{ formatConsonants(order.consonant) }}</span>
                                </div>
                            </td>

                            <td>
                                <span class="inactive__label" v-if="order?.feedback_type === 'incorrect'">{{
                                    capitalize(order?.feedback_type) }}</span>
                                <span class="active__label" v-if="order?.feedback_type === 'correct'">{{
                                    capitalize(order?.feedback_type) }}</span>
                            </td>

                            <td>{{ order?.transcription }}</td>

                            <td>
                                <router-link :to="`/admin/users/${order?.user_id}`">
                                    {{ order?.user?.firstname }} {{ order?.user?.lastname }}
                                </router-link>
                            </td>

                            <td>
                                <div class="playaudiobtn">
                                    <a href="javascript:void(0)" @click.prevent="playAudio(order)">
                                        <span class="play__icon">
                                            <img src="@/assets/images/audio/transcribe/play.png">
                                        </span>
                                    </a>
                                </div>
                            </td>
                        </tr>
                    </template>

                    <template v-else>
                        <tr>
                            <td colspan="8">
                                <div class="rlms-flex rlms-flex-col rlms-gap-30 rlms-text-center">
                                    <img :src="noAssessmentsImg" class="rlms-mx-auto" style="width: 100px">
                                    <p v-if="search || status">No transcription feedback found for the selected search
                                        or filter criteria.</p>
                                    <p v-else>No transcription feedback available.</p>
                                </div>
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>
    </div>

    <div class="rlms-w-100">
        <paginate v-model="page" :page-count="totalPages" :margin-pages="2" :prev-text="'<'" :next-text="'>'"
            :container-class="'pagination'" :page-class="'page-item'" :active-class="'active-page'"
            v-if="totalPages > 1" />
    </div>

    <PlayAudioModal v-if="selectedAssessmentItem" :item="selectedAssessmentItem"
        @close="selectedAssessmentItem = null" />
</template>

<script setup>
import api from '@/services/axios';
import { useLoaderStore } from '@/stores/loader';
import { debounce } from '@/utils/debounce';
import { handleError } from '@/utils/handleError';
import { capitalize, onMounted, ref, watch } from 'vue';
import paginate from 'vuejs-paginate-next';
import noAssessmentsImg from '@/assets/images/no-assessments.png';
import { formatConsonants } from '@/utils/format-helpers';
import PlayAudioModal from '@/components/PlayAudioModal.vue';

const page = ref(1);
const totalPages = ref(1);
const totalItems = ref(0);
const status = ref('');
const feedbackType = ref('');
const search = ref('');
const selectedAssessmentItem = ref(null);
const orders = ref([]);

const loader = useLoaderStore();

const getOrders = async () => {
    try {
        loader.show();

        const { data } = await api.get(`admin/assessments/feedback`, {
            params: {
                page: page.value,
                per_page: 10,
                status: status.value,
                search: search.value,
                feedback_type: feedbackType.value,
            }
        });

        orders.value = data?.data?.data || [];
        totalPages.value = data?.data?.last_page ?? 1;
        totalItems.value = data?.data?.total ?? 0;
    } catch (error) {
        handleError(error);
    } finally {
        loader.hide();
    }
};

onMounted(() => getOrders());

watch(page, () => getOrders());

watch([status, feedbackType], () => {
    page.value = 1;
    getOrders();
});

const handleSearch = debounce(() => {
    page.value = 1;
    getOrders();
}, 400);

const playAudio = async (item) => {
    try {
        loader.show();
        const { data } = await api.get(`/organizations/${item.orgId}/assessments/${item.aid}/attempt/${item?.assessment_attempt_id}`);
        selectedAssessmentItem.value = data.data;
    } catch (error) {
        selectedAssessmentItem.value = null;
        handleError(error);
    } finally {
        loader.hide();
    }
};

// Calculate serial number dynamically
const getSerialNumber = (index) => {
    const perPage = 10;
    return (page.value - 1) * perPage + (index + 1);
};
</script>
